// Native saves: file names for export, import checks, persistent storage, and safe handling of files the game
// hands to the player (rtw_export_file).
import type { Manifest } from '../../shared/manifest.ts';

/** The usual extension for a raw save of this kind, so emulators and cart writers recognise the file. */
export function saveExtension(platform: string, kind: string): string {
  if (platform === 'gba') return 'sav';
  return ({ eeprom: 'eep', sram: 'sra', flash: 'fla', controllerpak: 'mpk' } as Record<string, string>)[kind] ?? 'sav';
}

export function saveFileName(m: Manifest): string {
  return `${m.id}.${saveExtension(m.platform, m.save?.kind ?? 'none')}`;
}

export const MAX_SAVE_BYTES = 1 << 20;

/** Check an imported save file; returns an error message or null. */
export function checkImport(m: Manifest, bytes: Uint8Array): string | null {
  if (!m.save || m.save.kind === 'none') return 'This game has no saves.';
  if (!bytes.length) return 'That file is empty.';
  if (bytes.length > MAX_SAVE_BYTES) return 'That file is too big to be a save.';
  if (m.save.size && bytes.length > m.save.size * 2) return `That file is ${bytes.length} bytes; this game's ${m.save.kind} save is ${m.save.size} bytes.`;
  return null;
}

/**
 * Ask the browser to keep our storage (saves) under storage pressure. Returns true if persisted, false if the
 * browser said no, null if the API isn't there.
 */
export async function ensurePersisted(): Promise<boolean | null> {
  const s = (navigator as Navigator & { storage?: StorageManager }).storage;
  if (!s?.persist) return null;
  try {
    if (await s.persisted?.()) return true;
    return await s.persist();
  } catch {
    return false;
  }
}

// ---- files from rtw_export_file -----------------------------------------------------------------------------

/** MIME types we keep as given; anything else is downloaded as octet-stream (never rendered on our origin). */
const SAFE_MIME = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp', 'text/plain', 'text/csv',
  'application/json', 'application/octet-stream', 'application/zip', 'audio/wav', 'audio/x-wav', 'audio/ogg', 'audio/webm',
  'video/webm', 'video/mp4']);

export function safeMime(mime: string): string {
  const m = mime.trim().toLowerCase();
  return SAFE_MIME.has(m) ? m : 'application/octet-stream';
}

/** A plain file name: no paths, no control characters, at most 120 characters. */
export function safeFileName(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? '';
  // eslint-disable-next-line no-control-regex
  const clean = base.replace(/[\u0000-\u001f\u007f<>:"|?*]/g, '_').replace(/^\.+/, '').trim().slice(0, 120);
  return clean || 'file';
}

export interface GameFile { id: number; name: string; mime: string; bytes: Uint8Array; at: number }

/** Saved files are kept on the device per game up to this; beyond it the oldest go first. */
export const FILES_CAP = { bytes: 128 * 1024 * 1024, count: 200 } as const;

export function downloadBytes(name: string, mime: string, bytes: Uint8Array): void {
  const url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Copy to the clipboard: text as text, images as images. Returns false where the browser can't. */
export async function copyFile(f: GameFile): Promise<boolean> {
  try {
    if (f.mime.startsWith('text/') || f.mime === 'application/json') {
      await navigator.clipboard.writeText(new TextDecoder().decode(f.bytes));
      return true;
    }
    if (f.mime.startsWith('image/') && typeof ClipboardItem !== 'undefined') {
      const blob = new Blob([f.bytes as Uint8Array<ArrayBuffer>], { type: f.mime === 'image/png' ? 'image/png' : f.mime });
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
      return true;
    }
  } catch { /* denied or unsupported */ }
  return false;
}

export function canCopy(mime: string): boolean {
  return mime.startsWith('text/') || mime === 'application/json' || mime.startsWith('image/');
}
